import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';

// Runtime detection - works in both localhost and production
const API = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
  ? 'http://localhost:8001/api' 
  : '/api';

function TradeHistory({ user, refresh, onRefresh }) {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, today, week
  const token = localStorage.getItem('token');
  
  // Modal states
  const [alertModal, setAlertModal] = useState({ show: false, message: '', type: 'info' });
  const [confirmModal, setConfirmModal] = useState({ show: false, message: '', type: 'info', onConfirm: null, confirmData: null });
  
  const showAlert = (message, type = 'info') => setAlertModal({ show: true, message, type });
  const showConfirm = (message, onConfirm, confirmData = null) => setConfirmModal({ show: true, message, type: 'confirm', onConfirm, confirmData });

  useEffect(() => {
    console.log(' TradeHistory: Fetching trades, refresh value:', refresh);
    fetchTrades();
  }, [refresh]);

  const fetchTrades = async () => {
    console.log('📊 TradeHistory: Starting fetch...');
    setLoading(true);
    try {
      const response = await axios.get(`${API}/transactions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log(`✅ TradeHistory: Fetched ${response.data.length} total transactions`);
      
      // Filter only CLOSE transactions
      const closeTrades = response.data.filter(t => 
        t.transaction_type && (t.transaction_type.includes('CLOSE') || t.transaction_type === 'SELL')
      );
      console.log(`✅ TradeHistory: Filtered to ${closeTrades.length} close trades`);
      
      setTrades(closeTrades);
    } catch (error) {
      console.error(' TradeHistory: İşlem geçmişi yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTrades = trades.filter(trade => {
    if (filter === 'all') return true;
    const tradeDate = new Date(trade.timestamp || trade.created_at);
    const now = new Date();
    if (filter === 'today') {
      return tradeDate.toDateString() === now.toDateString();
    }
    if (filter === 'week') {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return tradeDate >= weekAgo;
    }
    return true;
  });

  // Modal render helper
  const renderModals = () => (
    <>
      {/* Site İçi Alert Modal */}
      {alertModal.show && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl w-full max-w-sm p-5 shadow-2xl">
            <div className="text-center">
              <div className="text-4xl mb-3">
                {alertModal.type === 'success' ? '✅' : alertModal.type === 'error' ? '❌' : 'ℹ️'}
              </div>
              <p className={`text-sm mb-4 whitespace-pre-line ${alertModal.type === 'success' ? 'text-green-400' : alertModal.type === 'error' ? 'text-red-400' : 'text-gray-300'}`}>
                {alertModal.message}
              </p>
              <button
                onClick={() => setAlertModal({ show: false, message: '', type: 'info' })}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Site İçi Confirm Modal */}
      {confirmModal.show && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl w-full max-w-sm p-5 shadow-2xl">
            <div className="text-center">
              <div className="text-4xl mb-3">⚠️</div>
              <p className="text-sm mb-4 text-gray-300 whitespace-pre-line">{confirmModal.message}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmModal({ show: false, message: '', type: 'info', onConfirm: null, confirmData: null })}
                  className="flex-1 py-2.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={() => {
                    if (confirmModal.onConfirm) confirmModal.onConfirm(confirmModal.confirmData);
                    setConfirmModal({ show: false, message: '', type: 'info', onConfirm: null, confirmData: null });
                  }}
                  className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                >
                  Evet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (loading) {
    return (
      <>
        <div className="text-gray-400 text-center py-4">Yükleniyor...</div>
        {renderModals()}
      </>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filtreler */}
      <div className="flex gap-2 overflow-x-auto">
        <button
          onClick={() => setFilter('all')}
          className={`px-3 py-2 rounded text-sm whitespace-nowrap ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'}`}
        >
          Tümü
        </button>
        <button
          onClick={() => setFilter('today')}
          className={`px-3 py-2 rounded text-sm whitespace-nowrap ${filter === 'today' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'}`}
        >
          Bugün
        </button>
        <button
          onClick={() => setFilter('week')}
          className={`px-3 py-2 rounded text-sm whitespace-nowrap ${filter === 'week' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'}`}
        >
          Bu Hafta
        </button>
      </div>

      {/* İşlemler */}
      {filteredTrades.length === 0 ? (
        <div className="bg-gray-700 rounded-lg p-4 sm:p-6 text-center">
          <div className="text-gray-400 text-sm">📋 İşlem geçmişi bulunamadı</div>
        </div>
      ) : (
        <div className="space-y-2 max-h-[200px] sm:max-h-[400px] overflow-y-auto overflow-x-hidden">
          {filteredTrades.map((trade) => (
            <div key={trade.id} className="bg-gray-700 rounded-lg p-2 sm:p-2.5 w-full">
              {/* Hisse Adı ve Tarih */}
              <div className="flex justify-between items-start gap-2 mb-1.5 w-full">
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold text-sm truncate">{trade.asset_symbol || trade.symbol}</div>
                  <div className="flex items-center gap-1 mt-0.5">
                    <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${
                      trade.transaction_type === 'CLOSE_STOPOUT' || trade.close_reason === 'MARGIN_STOPOUT'
                        ? 'bg-red-900 text-red-400'
                        : trade.transaction_type === 'CLOSE_TP' || trade.close_reason === 'TP_TRIGGERED' || trade.profit_loss >= 0 
                          ? 'bg-green-900 text-green-400' 
                          : 'bg-red-900 text-red-400'
                    }`}>
                      {trade.transaction_type === 'CLOSE_STOPOUT' || trade.close_reason === 'MARGIN_STOPOUT' 
                        ? 'STOP-OUT' 
                        : trade.transaction_type === 'CLOSE_TP' || trade.close_reason === 'TP_TRIGGERED'
                          ? 'TP' 
                          : trade.transaction_type === 'CLOSE_SL' || trade.close_reason === 'SL_TRIGGERED'
                            ? 'SL' 
                            : 'KAPAT'}
                    </span>
                    <span className="text-gray-400 text-xs">{trade.amount || trade.quantity} adet</span>
                  </div>
                </div>
                <div className="text-xs text-gray-400 shrink-0 text-right">
                  <div>{new Date(trade.timestamp || trade.created_at).toLocaleDateString('tr-TR', {day: '2-digit', month: '2-digit', year: 'numeric'})}</div>
                  <div>{new Date(trade.timestamp || trade.created_at).toLocaleTimeString('tr-TR', {hour: '2-digit', minute: '2-digit'})}</div>
                </div>
              </div>

              {/* Giriş, Çıkış, K/Z */}
              <div className="grid grid-cols-3 gap-1 text-xs w-full">
                <div>
                  <div className="text-gray-500">Giriş</div>
                  <div className="text-white font-mono">{getCurrencySymbol(user)}{(trade.entry_price || 0).toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-gray-500">Çıkış</div>
                  <div className="text-white font-mono">{getCurrencySymbol(user)}{(trade.price || 0).toFixed(2)}</div>
                </div>
                <div className="text-right">
                  <div className="text-gray-500">K/Z</div>
                  <div className={`font-bold ${
                    (trade.profit_loss || 0) >= 0 ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {(trade.profit_loss || 0) >= 0 ? '+' : ''}{formatCurrency(trade.profit_loss || 0, user)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {renderModals()}
    </div>
  );
}

export default TradeHistory;